import { expected, schQueryParams } from '@aloft/toolkit-shared';

import { useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';

import { CraftsmanRecommendCompsModal } from '../components/RecommendedCompSelection/CraftsmanRecommendCompsModal.tsx';
import { AddComparablesModal } from '../modules/analysis/modals/addcomparables/AddComparablesModal.tsx';
import { addComps } from '../redux/features/actions/addComps.ts';
import { setReferenceViewOpen } from '../redux/features/referenceBarUISlice.ts';
import { selectEffectiveDate } from '../redux/features/selectors/orderSelectors.ts';
import { selectCompPropertyDataEntities } from '../redux/features/selectors/selectCompPropertyDataEntities.ts';
import { selectSubjectPropertyDataEntity } from '../redux/features/selectors/selectSubjectPropertyDataEntity.ts';
import { useAppDispatch, useAppSelector } from '../redux/hooks.ts';

export function useCraftsmanAddCompModals(): {
  addCompsModal: JSX.Element | false;
  addRecommendedCompsModal: JSX.Element | false;
  openAddCompsModal: () => void;
  openAddRecommendedCompsModal: () => void;
} {
  const { orderId } = useParams();
  const dispatch = useAppDispatch();
  const effectiveDate = useAppSelector((state) => selectEffectiveDate(state));
  const subject = useAppSelector(selectSubjectPropertyDataEntity);
  const currentComps = useAppSelector(selectCompPropertyDataEntities);

  const compSequence = useAppSelector((state) => state.order.compSequence);

  const [isAddCompsModalOpen, setIsAddCompsModalOpen] = useState<boolean>(false);
  const [isAddRecommendedCompsModalOpen, setIsAddRecommendedCompsModalOpen] =
    useState<boolean>(false);

  const openAddCompsModal = useCallback(() => {
    setIsAddCompsModalOpen(true);
  }, []);

  const closeManualAddPropertiesModal = useCallback(() => {
    setIsAddCompsModalOpen(false);
  }, []);

  const addCompsModal = isAddCompsModalOpen && (
    <AddComparablesModal
      isModalOpen={isAddCompsModalOpen}
      onModalClose={closeManualAddPropertiesModal}
    />
  );

  const openAddRecommendedCompsModal = useCallback(() => {
    dispatch(setReferenceViewOpen(false));
    setIsAddRecommendedCompsModalOpen(true);

    const currentUrl = new URL(window.location.href);
    if (!currentUrl.searchParams.has(schQueryParams.enum.addComps)) {
      currentUrl.searchParams.set(schQueryParams.enum.addComps, 'true');
      window.history.replaceState({}, '', currentUrl.toString());
    }
  }, []);

  const closeAddRecommendedCompsModal = useCallback(() => {
    setIsAddRecommendedCompsModalOpen(false);

    const currentUrl = new URL(window.location.href);
    if (currentUrl.searchParams.has(schQueryParams.enum.addComps)) {
      currentUrl.searchParams.delete(schQueryParams.enum.addComps);
      window.history.replaceState({}, '', currentUrl.toString());
    }
  }, []);

  const addRecommendedCompsModal =
    isAddRecommendedCompsModalOpen && subject && effectiveDate ? (
      <CraftsmanRecommendCompsModal
        orderID={expected(orderId)}
        effectiveDate={effectiveDate}
        compSeq={compSequence}
        currentComps={currentComps}
        createComps={(comps) => dispatch(addComps(comps))}
        subject={subject}
        isModalOpen={isAddRecommendedCompsModalOpen}
        onModalClose={closeAddRecommendedCompsModal}
      />
    ) : (
      false
    );

  return {
    addCompsModal,
    addRecommendedCompsModal,
    openAddCompsModal,
    openAddRecommendedCompsModal,
  };
}
